/**
 * V154 — Notification bell + dropdown panel. Replaces the
 * navigate-to-engagement bell in Topbar.
 *
 * Polls `/api/workspaces/{id}/notifications` every 60s; surfaces the
 * unread count as a badge; opens a dropdown with the active list.
 * Per-notification card has a Retry action (for `cadence_failed` →
 * triggers `cadences/run-now` async) and a Dismiss action.
 *
 * Engagement unread count is intentionally separate now — it lives
 * in the sidebar inbox link. Mixing the two streams in one bell made
 * the "what does this number mean?" UX murky.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, Loader2, X, RefreshCw, AlertCircle } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cadenceRunNowApi, notificationsApi, type NotificationResponse } from '@/lib/api';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/Button';

interface Props {
  workspaceId: string;
}

export function NotificationBell({ workspaceId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data, isPending } = useQuery({
    queryKey: ['notifications', workspaceId],
    queryFn: () => notificationsApi.list(workspaceId, 25),
    refetchInterval: 60_000,
    enabled: !!workspaceId,
  });

  const unread = data?.unread ?? 0;
  // Memoised so the loading `[]` fallback keeps a stable identity — the
  // auto-mark-read effect below depends on `items`.
  const items = useMemo(() => data?.items ?? [], [data?.items]);

  // Click outside to close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Auto-mark-read on open — if the user opened the dropdown, they've
  // seen it. Server flips read_at; the unread badge clears on the next
  // refetch (which happens via invalidateQueries after each mutation).
  useEffect(() => {
    if (!open || items.length === 0) return;
    const unreadIds = items.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    Promise.all(unreadIds.map((id) => notificationsApi.markRead(workspaceId, id)))
      .catch(() => {})
      .finally(() => queryClient.invalidateQueries({ queryKey: ['notifications', workspaceId] }));
  }, [open, items, workspaceId, queryClient]);

  const dismissMutation = useMutation({
    mutationFn: (notificationId: string) => notificationsApi.dismiss(workspaceId, notificationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications', workspaceId] }),
  });

  const retryCadenceMutation = useMutation({
    mutationFn: async (n: NotificationResponse) => {
      await cadenceRunNowApi.trigger(workspaceId);
      // Dismiss the notification once the retry is dispatched.
      await notificationsApi.dismiss(workspaceId, n.id);
    },
    onSuccess: () => {
      toast.success(
        t(
          'notifications.cadence.retryDispatched',
          'Retry started — new posts will appear in Review as they complete.',
        ),
      );
      queryClient.invalidateQueries({ queryKey: ['notifications', workspaceId] });
    },
    onError: () =>
      toast.error(
        t('notifications.cadence.retryFailed', 'Could not start retry — try again in a moment.'),
      ),
  });

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={
          unread > 0
            ? t('topbar.notificationsUnread', { count: unread })
            : t('topbar.notifications')
        }
        aria-label={t('topbar.notifications')}
        className="mk-topbar-btn relative"
      >
        <Bell className="h-4 w-4" />
        <AnimatePresence>
          {unread > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="bg-accent absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] leading-none font-bold text-white shadow-[0_0_8px_rgba(232,84,10,0.6)]"
            >
              {unread > 9 ? '9+' : unread}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="border-border absolute top-full right-0 z-50 mt-2 w-96 overflow-hidden rounded-xl border bg-white shadow-xl dark:bg-[#1a1a20]"
          >
            <div className="border-border bg-subtle/50 flex items-center justify-between border-b px-4 py-3">
              <p className="text-text-primary text-sm font-semibold">
                {t('notifications.title', 'Notifications')}
              </p>
              <button
                type="button"
                onClick={() => navigate('/dashboard/engagement')}
                className="text-text-tertiary hover:text-text-secondary text-xs"
              >
                {t('notifications.viewEngagement', 'Engagement inbox →')}
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {isPending ? (
                <div className="text-text-tertiary flex items-center justify-center gap-2 p-6 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.loading', 'Loading…')}
                </div>
              ) : items.length === 0 ? (
                <div className="text-text-tertiary p-6 text-center text-sm">
                  {t('notifications.empty', 'No notifications')}
                </div>
              ) : (
                <ul className="divide-border divide-y">
                  {items.map((n) => (
                    <li
                      key={n.id}
                      className={
                        'p-4 transition-colors ' +
                        (n.read ? 'bg-white dark:bg-[#1a1a20]' : 'bg-accent/5')
                      }
                    >
                      <div className="flex items-start gap-3">
                        <NotificationIcon type={n.type} />
                        <div className="min-w-0 flex-1">
                          <p className="text-text-primary text-sm font-semibold">{n.title}</p>
                          {n.body && (
                            <p className="text-text-secondary mt-1 text-xs leading-relaxed">
                              {n.body}
                            </p>
                          )}
                          <div className="mt-2 flex items-center gap-2">
                            {n.actionType === 'retry_cadence' && (
                              <Button
                                disabled={retryCadenceMutation.isPending}
                                onClick={() => retryCadenceMutation.mutate(n)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
                              >
                                {retryCadenceMutation.isPending ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3 w-3" />
                                )}
                                {t('notifications.cadence.retry', 'Retry')}
                              </Button>
                            )}
                            <button
                              type="button"
                              onClick={() => dismissMutation.mutate(n.id)}
                              className="text-text-tertiary hover:text-text-secondary inline-flex items-center gap-1 px-2 py-1.5 text-xs"
                            >
                              <X className="h-3 w-3" />
                              {t('notifications.dismiss', 'Dismiss')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NotificationIcon({ type }: { type: string }) {
  if (type === 'cadence_failed') {
    return (
      <span className="bg-accent/15 text-accent flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
        <AlertCircle className="h-4 w-4" />
      </span>
    );
  }
  return (
    <span className="bg-subtle text-text-tertiary flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
      <Bell className="h-4 w-4" />
    </span>
  );
}
